import { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { registerPermissionsContext } from './index';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext();
registerPermissionsContext(PermissionsContext);

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();

  const [userRights, setUserRights] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rightsByModule, setRightsByModule] = useState({});

  /**
   * Helper to persist permissions locally and in state.
   * Call this from anywhere (e.g., after login, or when you receive rights via another flow).
   * Expected shape:
   * {
   *   direitos: Array<{ Nome: string, ... }>,
   *   direitosAgrupados: Record<string, any[]>,
   *   usuario: { perfilNome?: string, ... }
   * }
   */
  const bootstrapPermissions = useCallback((data = {}) => {
    const direitos = data.direitos || [];
    const direitosAgrupados = data.direitosAgrupados || {};
    const usuario = data.usuario || null;

    setUserRights(direitos);
    setRightsByModule(direitosAgrupados);
    setUserProfile(usuario);

    try {
      localStorage.setItem('userRights', JSON.stringify(direitos));
      localStorage.setItem('userProfile', JSON.stringify(usuario));
      localStorage.setItem('rightsByModule', JSON.stringify(direitosAgrupados));
    } catch {
      // ignore storage errors
    }
  }, []);

  /**
   * Loads permissions from localStorage only.
   * This replaces the previous network call to `${API_BASE_URL}/direitos/usuario/meus-direitos`.
   */
  const loadCachedPermissions = useCallback(() => {
    setLoading(true);
    try {
      const cachedRights = localStorage.getItem('userRights');
      const cachedProfile = localStorage.getItem('userProfile');
      const cachedRightsByModule = localStorage.getItem('rightsByModule');

      if (cachedRights || cachedProfile || cachedRightsByModule) {
        setUserRights(cachedRights ? JSON.parse(cachedRights) : []);
        setUserProfile(cachedProfile ? JSON.parse(cachedProfile) : null);
        setRightsByModule(cachedRightsByModule ? JSON.parse(cachedRightsByModule) : {});
      } else {
        // Nothing cached: keep empty defaults (non-breaking — checks will return false)
        setUserRights([]);
        setUserProfile(null);
        setRightsByModule({});
      }
    } catch {
      setUserRights([]);
      setUserProfile(null);
      setRightsByModule({});
    } finally {
      setLoading(false);
    }
  }, []);

  const hasRight = useCallback(
    (rightName) => {
      if (!userRights.length) return false;
      return userRights.some((right) => right.Nome === rightName);
    },
    [userRights]
  );

  const hasAnyRight = useCallback(
    (rightNames) => {
      if (!userRights.length || !rightNames?.length) return false;
      return rightNames.some((rightName) => userRights.some((right) => right.Nome === rightName));
    },
    [userRights]
  );

  const hasAllRights = useCallback(
    (rightNames) => {
      if (!userRights.length || !rightNames?.length) return false;
      return rightNames.every((rightName) => userRights.some((right) => right.Nome === rightName));
    },
    [userRights]
  );

  const hasProfile = useCallback(
    (profileName) => {
      if (!userProfile) return false;
      return userProfile.perfilNome === profileName;
    },
    [userProfile]
  );

  const hasModuleRights = useCallback(
    (moduleName) => {
      if (!rightsByModule[moduleName]) return false;
      return rightsByModule[moduleName].length > 0;
    },
    [rightsByModule]
  );

  const getModuleRights = useCallback(
    (moduleName) => {
      return rightsByModule[moduleName] || [];
    },
    [rightsByModule]
  );

  const clearPermissionsCache = useCallback(() => {
    try {
      localStorage.removeItem('userRights');
      localStorage.removeItem('userProfile');
      localStorage.removeItem('rightsByModule');
    } catch {
      // ignore storage errors
    }
    setUserRights([]);
    setUserProfile(null);
    setRightsByModule({});
  }, []);

  /**
   * Public API kept for compatibility:
   * Previously re-fetched from backend; now it clears cache and reloads from cache (no-op if empty).
   * If you integrate a new source of permissions, call `bootstrapPermissions` before/after this.
   */
  const reloadPermissions = useCallback(() => {
    clearPermissionsCache();
    loadCachedPermissions();
  }, [clearPermissionsCache, loadCachedPermissions]);

  // When user changes, load from cache for authenticated internal users; otherwise clear.
  useEffect(() => {
    if (user?.token && !user?.isExternalUser) {
      loadCachedPermissions();
    } else {
      clearPermissionsCache();
    }
  }, [user?.token, user?.isExternalUser, loadCachedPermissions, clearPermissionsCache]);

  // Initial cache hydrate for authenticated non-external users (preserves old behavior).
  useEffect(() => {
    if (user?.token && !user?.isExternalUser) {
      try {
        const cachedRights = localStorage.getItem('userRights');
        const cachedProfile = localStorage.getItem('userProfile');
        const cachedRightsByModule = localStorage.getItem('rightsByModule');

        if (cachedRights && cachedProfile) {
          setUserRights(JSON.parse(cachedRights));
          setUserProfile(JSON.parse(cachedProfile));
          setRightsByModule(cachedRightsByModule ? JSON.parse(cachedRightsByModule) : {});
        }
      } catch {
        // ignore
      }
    }
  }, [user?.token, user?.isExternalUser]);

  const value = useMemo(() => ({
    userRights,
    userProfile,
    rightsByModule,
    loading,
    hasRight,
    hasAnyRight,
    hasAllRights,
    hasProfile,
    hasModuleRights,
    getModuleRights,
    reloadPermissions,
    clearPermissionsCache,
    bootstrapPermissions,
  }), [
    userRights,
    userProfile,
    rightsByModule,
    loading,
    hasRight,
    hasAnyRight,
    hasAllRights,
    hasProfile,
    hasModuleRights,
    getModuleRights,
    reloadPermissions,
    clearPermissionsCache,
    bootstrapPermissions,
  ]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions deve ser usado dentro de um PermissionsProvider');
  }
  return context;
};
